// src/pages/ccp/PrepCcpStartPage.js
// Start a Prep CCP batch (cook+cool for an intermediate product) — see docs/PREP_CCP_MODULE.md.
// Runs completely independently of whatever the main Baking CCP line is doing; the only
// concurrency rule is one active run per intermediate product at a time.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import prepCcpService from '../../services/prepCcpService';
import productService from '../../services/productService';
import ingredientService from '../../services/ingredientService';
import ingredientInventoryService from '../../services/ingredientInventoryService';
import SearchableSelect from '../../components/SearchableSelect';
import { usePermissions } from '../../context/PermissionContext';
import { useLanguage, t } from '../../context/LanguageContext';
import { generateLotCode } from '../../utils/lotCodeUtils';

const LB_TO_KG = 0.453592;

export default function PrepCcpStartPage() {
  const { lang } = useLanguage();
  const { companySettings } = usePermissions();
  const navigate = useNavigate();
  const lotCodeFormat = companySettings?.lotCodeFormat || null;

  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [activeRuns, setActiveRuns] = useState([]);
  const [lotCodesCache, setLotCodesCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const [productId, setProductId] = useState('');
  const [lotCode, setLotCode] = useState(generateLotCode(lotCodeFormat));
  const [productionDate, setProductionDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([{ ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'kg', expectedQtyKg: null }]);
  const [rawWeightOverride, setRawWeightOverride] = useState('');
  const [loadingRecipe, setLoadingRecipe] = useState(false);

  // Auto-populate ingredient rows from the intermediate product's own recipe (direct + base
  // recipe, same flattened shape AddLog.js/BakingCcpStartPage.js use) — the operator still has to
  // weigh and enter the real quantity, but shouldn't have to remember/re-pick which ingredients
  // go into it every time.
  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoadingRecipe(true);
    productService.getProductRecipe(productId)
      .then(raw => {
        if (cancelled) return;
        const allIngredients = Array.isArray(raw) ? [] : (raw.allIngredients || []);
        if (allIngredients.length === 0) {
          setRows([{ ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'kg', expectedQtyKg: null }]);
          return;
        }
        setRows(allIngredients.map(ing => ({
          ingredientId: String(ing.ingredientId),
          ingredientLotCode: '',
          quantity: '',
          uom: 'kg',
          expectedQtyKg: ing.expectedQuantityKg ?? null,
        })));
        for (const ing of allIngredients) {
          ingredientInventoryService.getLotCodesAvailable(ing.ingredientId)
            .then(lcs => setLotCodesCache(prev => ({ ...prev, [String(ing.ingredientId)]: lcs })))
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingRecipe(false); });
    return () => { cancelled = true; };
  }, [productId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, ings, runs] = await Promise.all([
        productService.getProducts({ includeInactive: true }),
        ingredientService.getIngredients(),
        prepCcpService.getActiveRuns(),
      ]);
      const list = (Array.isArray(prods) ? prods : prods?.products || []).filter(p => p.isIntermediate);
      setProducts(list);
      setIngredients(Array.isArray(ings) ? ings : []);
      setActiveRuns(Array.isArray(runs) ? runs : []);
    } catch {
      setError(t('Failed to load page data.', 'Error al cargar los datos.', lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRow = () => setRows(r => [...r, { ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'kg', expectedQtyKg: null }]);
  const removeRow = (idx) => setRows(r => r.length <= 1 ? r : r.filter((_, i) => i !== idx));
  const setRow = (idx, field, value) => {
    setRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    if (field === 'ingredientId' && value && !lotCodesCache[value]) {
      ingredientInventoryService.getLotCodesAvailable(value)
        .then(lcs => setLotCodesCache(prev => ({ ...prev, [value]: lcs })))
        .catch(() => {});
    }
  };

  const toKg = (qty, uom) => {
    const n = Number(qty);
    if (!Number.isFinite(n)) return 0;
    return uom === 'lb' ? n * LB_TO_KG : n;
  };

  const summedWeightKg = rows.reduce((sum, r) => sum + toKg(r.quantity, r.uom), 0);

  const activeRunForProduct = productId ? activeRuns.find(r => String(r.productId) === String(productId)) : null;

  const handleStart = async () => {
    setError('');
    if (!productId) { setError(t('Select an intermediate product.', 'Selecciona un producto intermedio.', lang)); return; }
    if (!lotCode.trim()) { setError(t('Lot code is required.', 'El código de lote es obligatorio.', lang)); return; }
    const validRows = rows.filter(r => r.ingredientId);
    if (validRows.length === 0) { setError(t('Add at least one ingredient.', 'Agrega al menos un ingrediente.', lang)); return; }

    setStarting(true);
    try {
      const run = await prepCcpService.startRun({
        productId,
        lotCode: lotCode.trim(),
        productionDate,
        ingredientLots: validRows.map(r => ({
          ingredientId: r.ingredientId,
          ingredientLotCode: r.ingredientLotCode,
          quantityKg: r.quantity !== '' ? toKg(r.quantity, r.uom) : null,
        })),
        rawInputWeightKgOverride: rawWeightOverride !== '' ? Number(rawWeightOverride) : null,
      });
      navigate(`/ccp/prep/live/${run.id}`);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.runId) {
        navigate(`/ccp/prep/live/${err.response.data.runId}`);
        return;
      }
      setError(err?.response?.data?.message || t('Failed to start run.', 'Error al iniciar la corrida.', lang));
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <div className="container py-5 text-center"><span className="spinner-border" /></div>;

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0">🍲 {t('Start Prep Batch', 'Iniciar Lote de Preparación', lang)}</h4>
        <Link to="/ccp/prep/config" className="btn btn-outline-secondary btn-sm">⚙️ {t('Critical Limits', 'Límites Críticos', lang)}</Link>
      </div>

      {activeRuns.length > 0 && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
          <strong>{t('Currently active prep batches:', 'Lotes de preparación activos:', lang)}</strong>{' '}
          {activeRuns.map(r => (
            <Link key={r.id} to={`/ccp/prep/live/${r.id}`} className="me-2">
              {r.Product?.name} ({r.Batch?.lotCode}) — {r.status}
            </Link>
          ))}
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">{t('Intermediate Product', 'Producto Intermedio', lang)}</label>
              <SearchableSelect
                options={products.map(p => ({ value: String(p.id), label: p.name }))}
                value={productId}
                onChange={setProductId}
                placeholder={t('Select product…', 'Selecciona producto…', lang)}
              />
              {products.length === 0 && (
                <div className="form-text text-danger">
                  {t('No intermediate products configured yet.', 'Aún no hay productos intermedios configurados.', lang)}
                </div>
              )}
              {activeRunForProduct && (
                <div className="form-text text-warning">
                  ⚠ {t('This product already has an active run.', 'Este producto ya tiene una corrida activa.', lang)}{' '}
                  <Link to={`/ccp/prep/live/${activeRunForProduct.id}`}>{t('Open it', 'Ábrelo', lang)}</Link>
                </div>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">{t('Lot Code', 'Código de Lote', lang)}</label>
              <div className="input-group">
                <input className="form-control" value={lotCode} onChange={e => setLotCode(e.target.value)} />
                <button type="button" className="btn btn-outline-secondary" onClick={() => setLotCode(generateLotCode(lotCodeFormat))}>↻</button>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">{t('Production Date', 'Fecha de Producción', lang)}</label>
              <input type="date" className="form-control" value={productionDate} onChange={e => setProductionDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-semibold mb-0">
              {t('Ingredients (raw mise en place)', 'Ingredientes (mise en place crudo)', lang)}
              {loadingRecipe && <span className="spinner-border spinner-border-sm ms-2" />}
            </label>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addRow}>+ {t('Add', 'Agregar', lang)}</button>
          </div>
          {productId && rows.length === 1 && !rows[0].ingredientId && !loadingRecipe && (
            <div className="text-muted small mb-2">
              {t('This product has no recipe defined yet — add ingredients manually below, or build its recipe first in Products.',
                 'Este producto aún no tiene receta definida — agrega ingredientes manualmente abajo, o primero crea su receta en Productos.', lang)}
            </div>
          )}
          {rows.map((row, idx) => {
            const available = lotCodesCache[row.ingredientId] || [];
            return (
              <div key={idx} className="row g-2 mb-2 align-items-start">
                <div className="col-md-4">
                  <SearchableSelect
                    size="sm"
                    options={ingredients.map(i => ({ value: String(i.id), label: i.name }))}
                    value={row.ingredientId}
                    onChange={v => setRow(idx, 'ingredientId', v)}
                    placeholder={t('Ingredient…', 'Ingrediente…', lang)}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    className="form-control form-control-sm" list={`prep-lc-${idx}`}
                    placeholder={t('Lot code', 'Código de lote', lang)}
                    value={row.ingredientLotCode}
                    onChange={e => setRow(idx, 'ingredientLotCode', e.target.value)}
                  />
                  <datalist id={`prep-lc-${idx}`}>
                    {available.map(a => <option key={a.lotCode} value={a.lotCode}>{Number(a.remainingKg).toFixed(1)} kg remaining</option>)}
                  </datalist>
                </div>
                <div className="col-md-2">
                  <input type="number" className="form-control form-control-sm"
                    placeholder={row.expectedQtyKg != null ? Number(row.expectedQtyKg).toFixed(3) : '0'}
                    value={row.quantity} onChange={e => setRow(idx, 'quantity', e.target.value)} />
                  {row.expectedQtyKg != null && (
                    <div className="text-muted" style={{ fontSize: 10 }}>
                      {t('recipe:', 'receta:', lang)} {Number(row.expectedQtyKg).toFixed(3)} kg
                    </div>
                  )}
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={row.uom} onChange={e => setRow(idx, 'uom', e.target.value)}>
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                  </select>
                </div>
                <div className="col-md-1">
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow(idx)} disabled={rows.length <= 1}>&times;</button>
                </div>
              </div>
            );
          })}

          <div className="row g-2 mt-2 align-items-center">
            <div className="col-md-6">
              <div className="text-muted small">
                {t('Auto-summed raw input weight:', 'Peso de entrada crudo (auto-sumado):', lang)} <strong>{summedWeightKg.toFixed(3)} kg</strong>
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-center gap-2">
              <label className="form-label small mb-0 text-nowrap">{t('Override (optional):', 'Anular (opcional):', lang)}</label>
              <input type="number" className="form-control form-control-sm" placeholder={summedWeightKg.toFixed(3)}
                value={rawWeightOverride} onChange={e => setRawWeightOverride(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <button className="btn btn-primary btn-lg" onClick={handleStart} disabled={starting}>
        {starting ? t('Starting…', 'Iniciando…', lang) : `🔥 ${t('Start Cooking', 'Comenzar a Cocinar', lang)}`}
      </button>
    </div>
  );
}
